import React, { useCallback, useRef, useEffect, useState } from 'react';
import './DayNotesEditor.css';

// contentEditable sync: only update when dateKey changes (switching days) to avoid overwriting while typing
function useSyncContentEditable(ref, value, dateKey) {
  const lastKeyRef = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (dateKey !== lastKeyRef.current) {
      lastKeyRef.current = dateKey;
      el.innerHTML = value || '';
    }
  }, [ref, value, dateKey]);
}

// Journal templates inspired by TradeZella-style daily planning
const JOURNAL_TEMPLATES = {
  preMarket: {
    label: 'Pre Market Plan',
    html: `<p><strong class="template-highlight-yellow">Pre Market Game Plan</strong></p><ul><li><strong>Market:</strong> </li><li><strong>Watchlist:</strong> </li><li><strong>Key levels:</strong> </li></ul><p><strong class="template-highlight-blue">Day Recap</strong></p><ul><li><strong>Mistakes I made:</strong> </li><li><strong>What I did great:</strong> </li><li><strong>Reinforcement to myself:</strong> </li></ul>`,
  },
  dayRecap: {
    label: 'Day Recap',
    html: `<p><strong>Day Recap</strong></p><ul><li><strong>Mistakes I made:</strong> </li><li><strong>What I did great:</strong> </li><li><strong>Reinforcement to myself:</strong> </li></ul>`,
  },
  fullJournal: {
    label: 'Full Journal',
    html: `<p><strong>Pre Market Plan</strong></p><ul><li>Market: </li><li>Watchlist: </li></ul><p><strong>Day Recap</strong></p><ul><li>Mistakes: </li><li>Wins: </li><li>Reinforcement: </li></ul><p><strong>Overall Recap</strong></p><p></p>`,
  },
};

function DayNotesEditor({
  value,
  onChange,
  dateKey = null,
  placeholder = "Add notes about today's trading...",
}) {
  const editorRef = useRef(null);
  const [savedFeedback, setSavedFeedback] = useState(false);
  useSyncContentEditable(editorRef, value, dateKey);

  const emitChange = useCallback(() => {
    const el = editorRef.current;
    if (!el || !onChange) return;
    const html = el.innerHTML;
    if (html === '<br>' || html === '<br/>') {
      onChange('');
    } else {
      onChange(html);
    }
  }, [onChange]);

  const handleSave = useCallback(() => {
    emitChange();
    setSavedFeedback(true);
    setTimeout(() => setSavedFeedback(false), 1500);
  }, [emitChange]);

  const execCmd = useCallback((cmd, value = null) => {
    document.execCommand(cmd, false, value);
    editorRef.current?.focus();
    emitChange();
  }, [emitChange]);

  const insertTemplate = useCallback((templateKey) => {
    const t = JOURNAL_TEMPLATES[templateKey];
    if (!t || !editorRef.current) return;
    const el = editorRef.current;
    el.focus();
    document.execCommand('insertHTML', false, t.html);
    emitChange();
  }, [emitChange]);

  return (
    <div className="day-notes-editor">
      <div className="day-notes-toolbar">
        <select
          className="day-notes-template-select"
          onChange={(e) => {
            const v = e.target.value;
            if (v) { insertTemplate(v); e.target.value = ''; }
          }}
        >
          <option value="">Insert template...</option>
          {Object.entries(JOURNAL_TEMPLATES).map(([k, t]) => (
            <option key={k} value={k}>{t.label}</option>
          ))}
        </select>
        <div className="day-notes-toolbar-btns">
          <button type="button" onClick={() => execCmd('bold')} title="Bold">B</button>
          <button type="button" onClick={() => execCmd('italic')} title="Italic">I</button>
          <button type="button" onClick={() => execCmd('underline')} title="Underline">U</button>
          <button type="button" onClick={() => execCmd('insertUnorderedList')} title="Bullet list">•</button>
          <button type="button" onClick={() => execCmd('insertOrderedList')} title="Numbered list">1.</button>
          <button type="button" onClick={() => execCmd('formatBlock', 'blockquote')} title="Quote">"</button>
        </div>
        <button type="button" className={`day-notes-save-btn ${savedFeedback ? 'saved' : ''}`} onClick={handleSave}>
          {savedFeedback ? 'Saved' : 'Save'}
        </button>
      </div>
      <div
        ref={editorRef}
        className="day-notes-content"
        contentEditable
        data-placeholder={placeholder}
        onInput={emitChange}
        onBlur={emitChange}
        suppressContentEditableWarning
      />
    </div>
  );
}

export default DayNotesEditor;
